import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, SquarePlus, X, CheckCircle2 } from "lucide-react";
import { useInstall, isMobileDevice } from "@/lib/install";

const KEY = "mulankan_install_dismissed";

export const IOSSteps = ({ testId = "ios-install-steps" }) => (
  <ol className="space-y-2 text-sm text-white/75" data-testid={testId}>
    <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-white/[0.08] text-[11px] flex items-center justify-center">1</span>Tap <Share size={15} className="text-[var(--accent)]" /> <b>Share</b> in Safari's toolbar</li>
    <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-white/[0.08] text-[11px] flex items-center justify-center">2</span>Choose <SquarePlus size={15} className="text-[var(--accent)]" /> <b>Add to Home Screen</b></li>
    <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-white/[0.08] text-[11px] flex items-center justify-center">3</span>Tap <b>Add</b> — open Mulankan from your home screen</li>
  </ol>
);

export function InstallSection() {
  const { installed, canPrompt, ios, install } = useInstall();
  const [steps, setSteps] = useState(false);
  return (
    <section className="mb-10" data-testid="install-section">
      <div className="eyebrow mb-4">Phone app</div>
      <div className="panel p-6 max-w-3xl">
        <div className="flex items-start gap-4 mb-5">
          <img src="/icons/icon-192.png" alt="Mulankan app icon" className="w-12 h-12 rounded-2xl" />
          <p className="text-sm text-white/60">Add Mulankan to your iPhone or Android home screen. It opens full-screen like a real app, with no browser bars.</p>
        </div>
        {installed ? (
          <p className="text-sm text-emerald-300 flex items-center gap-2" data-testid="install-status-installed"><CheckCircle2 size={16} /> You're using the installed app.</p>
        ) : canPrompt ? (
          <button onClick={install} data-testid="install-app-button" className="btn-accent text-sm"><Download size={15} /> Install app</button>
        ) : ios ? (
          <IOSSteps testId="settings-ios-install-steps" />
        ) : (
          <>
            <button onClick={() => setSteps((v) => !v)} data-testid="install-how-button" className="btn-ghost text-sm"><Download size={15} /> How to install</button>
            {steps && (
              <div className="mt-4 space-y-2 text-sm text-white/70" data-testid="install-manual-steps">
                <p><b>Android (Chrome):</b> tap the ⋮ menu, then <b>Install app</b> or <b>Add to Home screen</b>.</p>
                <p><b>iPhone (Safari):</b> tap Share, then <b>Add to Home Screen</b>.</p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default function InstallBanner() {
  const { installed, canPrompt, ios, install } = useInstall();
  const [hidden, setHidden] = useState(() => localStorage.getItem(KEY) === "1");
  const [steps, setSteps] = useState(false);
  const { pathname } = useLocation();
  const [micOpen, setMicOpen] = useState(false);
  useEffect(() => {
    const on = (e) => setMicOpen(e.detail);
    window.addEventListener("mulankan:floating", on);
    return () => window.removeEventListener("mulankan:floating", on);
  }, []);
  if (pathname === "/listen" || micOpen || hidden || installed || !isMobileDevice() || (!canPrompt && !ios)) return null;
  const close = () => { localStorage.setItem(KEY, "1"); setHidden(true); };
  return (
    <div className="fixed left-4 right-4 bottom-24 z-40 panel p-4 shadow-2xl backdrop-blur-xl bg-[#17130F]/95 animate-in fade-in slide-in-from-bottom-4" data-testid="install-banner">
      <div className="flex items-center gap-3">
        <img src="/icons/icon-192.png" alt="" className="w-11 h-11 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[var(--ink)]">Get the Mulankan app</div>
          <div className="text-xs text-white/50">Full-screen, one tap from your home screen.</div>
        </div>
        <button onClick={ios ? () => setSteps((v) => !v) : async () => { await install(); close(); }}
          data-testid="install-banner-action" className="btn-accent text-xs shrink-0">{ios ? "How" : "Install"}</button>
        <button onClick={close} aria-label="Dismiss" data-testid="install-banner-close" className="p-1 text-white/40 hover:text-white transition-colors shrink-0"><X size={16} /></button>
      </div>
      {ios && steps && <div className="mt-4 border-t border-white/[0.06] pt-4"><IOSSteps testId="banner-ios-install-steps" /></div>}
    </div>
  );
}
